/** Matches backend ProcedureCodeTreatArea. */
export enum ProcedureCodeTreatArea {
  None = 0,
  Surf = 1,
  Tooth = 2,
  Mouth = 3,
  Quad = 4,
  Sextant = 5,
  Arch = 6,
  ToothRange = 7,
}

export type SurfaceSelectionTab = "surface" | "quad" | "sextant" | "arch";

export interface TreatAreaCapabilities {
  needsTooth: boolean;
  needsSurface: boolean;
  needsRange: boolean;
  /** Surface panel tabs enabled for this step (union across codes). */
  allowedSurfaceTabs: SurfaceSelectionTab[];
  /** Distinct treatArea values from codes in the step. */
  treatAreas: ProcedureCodeTreatArea[];
}

export const EMPTY_TREAT_AREA_CAPABILITIES: TreatAreaCapabilities = {
  needsTooth: false,
  needsSurface: false,
  needsRange: false,
  allowedSurfaceTabs: [],
  treatAreas: [],
};

export const ALL_SURFACE_TABS: SurfaceSelectionTab[] = [
  "surface",
  "quad",
  "sextant",
  "arch",
];

type TreatAreaFlagSource = {
  needsTooth?: boolean | null;
  needsSurface?: boolean | null;
  needsRange?: boolean | null;
  treatArea?: number | string | null;
};

function parseTreatArea(
  value?: number | string | null,
): ProcedureCodeTreatArea | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric)) return null;
  if (
    numeric === ProcedureCodeTreatArea.None ||
    numeric === ProcedureCodeTreatArea.Surf ||
    numeric === ProcedureCodeTreatArea.Tooth ||
    numeric === ProcedureCodeTreatArea.Mouth ||
    numeric === ProcedureCodeTreatArea.Quad ||
    numeric === ProcedureCodeTreatArea.Sextant ||
    numeric === ProcedureCodeTreatArea.Arch ||
    numeric === ProcedureCodeTreatArea.ToothRange
  ) {
    return numeric;
  }
  return null;
}

/**
 * Tabs implied by a single treatArea enum value.
 * Quad and Sextant share both tabs — providers may switch between them,
 * but selection stays XOR (one family at a time) in the surface panel.
 */
export function surfaceTabsForTreatArea(
  treatArea: ProcedureCodeTreatArea | null,
): SurfaceSelectionTab[] {
  switch (treatArea) {
    case ProcedureCodeTreatArea.Surf:
      return ["surface"];
    case ProcedureCodeTreatArea.Quad:
    case ProcedureCodeTreatArea.Sextant:
      return ["quad", "sextant"];
    case ProcedureCodeTreatArea.Arch:
      return ["arch"];
    default:
      return [];
  }
}

function flagsFromTreatArea(treatArea: ProcedureCodeTreatArea | null): {
  needsTooth: boolean;
  needsSurface: boolean;
  needsRange: boolean;
  allowedSurfaceTabs: SurfaceSelectionTab[];
} {
  switch (treatArea) {
    case ProcedureCodeTreatArea.Tooth:
      return {
        needsTooth: true,
        needsSurface: false,
        needsRange: false,
        allowedSurfaceTabs: [],
      };
    case ProcedureCodeTreatArea.Surf:
      return {
        needsTooth: true,
        needsSurface: true,
        needsRange: false,
        allowedSurfaceTabs: ["surface"],
      };
    case ProcedureCodeTreatArea.ToothRange:
      return {
        needsTooth: false,
        needsSurface: false,
        needsRange: true,
        allowedSurfaceTabs: [],
      };
    case ProcedureCodeTreatArea.Quad:
      return {
        needsTooth: false,
        needsSurface: true,
        needsRange: false,
        // Allow sextant as an alternative; UI keeps Q/S mutually exclusive.
        allowedSurfaceTabs: ["quad", "sextant"],
      };
    case ProcedureCodeTreatArea.Sextant:
      return {
        needsTooth: false,
        needsSurface: true,
        needsRange: false,
        // Allow quad as an alternative; UI keeps Q/S mutually exclusive.
        allowedSurfaceTabs: ["quad", "sextant"],
      };
    case ProcedureCodeTreatArea.Arch:
      return {
        needsTooth: false,
        needsSurface: true,
        needsRange: false,
        allowedSurfaceTabs: ["arch"],
      };
    case ProcedureCodeTreatArea.Mouth:
    case ProcedureCodeTreatArea.None:
    default:
      return {
        needsTooth: false,
        needsSurface: false,
        needsRange: false,
        allowedSurfaceTabs: [],
      };
  }
}

function uniqueTabs(tabs: SurfaceSelectionTab[]): SurfaceSelectionTab[] {
  const order = ALL_SURFACE_TABS;
  return order.filter((tab) => tabs.includes(tab));
}

export function normalizeTreatAreaCapabilities(
  source?: TreatAreaFlagSource | null,
): TreatAreaCapabilities {
  const treatArea = parseTreatArea(source?.treatArea);
  const fromEnum = flagsFromTreatArea(treatArea);
  const hasSpecificTreatArea =
    treatArea != null &&
    treatArea !== ProcedureCodeTreatArea.None &&
    treatArea !== ProcedureCodeTreatArea.Mouth;

  // When treatArea is a concrete mode, it is the source of truth for selectors.
  // Otherwise fall back to legacy needs* flags.
  const needsTooth = hasSpecificTreatArea
    ? fromEnum.needsTooth
    : fromEnum.needsTooth || Boolean(source?.needsTooth);
  const needsSurface = hasSpecificTreatArea
    ? fromEnum.needsSurface
    : fromEnum.needsSurface || Boolean(source?.needsSurface);
  const needsRange = hasSpecificTreatArea
    ? fromEnum.needsRange
    : fromEnum.needsRange || Boolean(source?.needsRange);

  let allowedSurfaceTabs = fromEnum.allowedSurfaceTabs;
  if (allowedSurfaceTabs.length === 0 && needsSurface && !hasSpecificTreatArea) {
    // Legacy: needsSurface without Quad/Sextant/Arch/Surf treatArea → all tabs.
    allowedSurfaceTabs = [...ALL_SURFACE_TABS];
  }

  return {
    needsTooth,
    needsSurface,
    needsRange,
    allowedSurfaceTabs: uniqueTabs(allowedSurfaceTabs),
    treatAreas: treatArea != null ? [treatArea] : [],
  };
}

/** OR across all codes in the step — any true enables that selector. */
export function aggregateTreatAreaCapabilities(
  sources: Array<TreatAreaFlagSource | TreatAreaCapabilities | null | undefined>,
): TreatAreaCapabilities {
  return sources.reduce<TreatAreaCapabilities>(
    (acc, source) => {
      const normalized =
        source &&
        "allowedSurfaceTabs" in source &&
        Array.isArray(source.allowedSurfaceTabs) &&
        "treatAreas" in source
          ? (source as TreatAreaCapabilities)
          : normalizeTreatAreaCapabilities(source as TreatAreaFlagSource);

      return {
        needsTooth: acc.needsTooth || normalized.needsTooth,
        needsSurface: acc.needsSurface || normalized.needsSurface,
        needsRange: acc.needsRange || normalized.needsRange,
        allowedSurfaceTabs: uniqueTabs([
          ...acc.allowedSurfaceTabs,
          ...normalized.allowedSurfaceTabs,
        ]),
        treatAreas: Array.from(
          new Set([...acc.treatAreas, ...normalized.treatAreas]),
        ),
      };
    },
    { ...EMPTY_TREAT_AREA_CAPABILITIES, allowedSurfaceTabs: [], treatAreas: [] },
  );
}

export function canSelectTeeth(capabilities: TreatAreaCapabilities): boolean {
  return capabilities.needsTooth || capabilities.needsRange;
}

export function canSelectSurfaces(capabilities: TreatAreaCapabilities): boolean {
  return (
    capabilities.needsSurface || capabilities.allowedSurfaceTabs.length > 0
  );
}

export function allowsToothSurfaceTab(
  capabilities: TreatAreaCapabilities,
): boolean {
  return capabilities.allowedSurfaceTabs.includes("surface");
}

export function allowsAreaSurfaceTab(
  capabilities: TreatAreaCapabilities,
): boolean {
  return capabilities.allowedSurfaceTabs.some((tab) => tab !== "surface");
}

/** Preferred tab when opening the surface panel. */
export function getDefaultSurfaceTab(
  capabilities: TreatAreaCapabilities,
): SurfaceSelectionTab {
  const allowed = capabilities.allowedSurfaceTabs;
  if (allowed.length === 0) return "surface";

  // Prefer the code's primary treatArea when both quad and sextant are allowed.
  const prefersSextant =
    capabilities.treatAreas.includes(ProcedureCodeTreatArea.Sextant) &&
    !capabilities.treatAreas.includes(ProcedureCodeTreatArea.Quad);
  if (prefersSextant && allowed.includes("sextant")) {
    return "sextant";
  }
  if (
    capabilities.treatAreas.includes(ProcedureCodeTreatArea.Quad) &&
    allowed.includes("quad")
  ) {
    return "quad";
  }

  return allowed[0];
}

/** Max teeth selectable in the tooth panel (per-step limit). */
export function getMaxTeethAllowed(
  capabilities: TreatAreaCapabilities,
  absoluteMax = 8,
): number {
  if (!canSelectTeeth(capabilities)) return 0;
  return absoluteMax;
}

/** needsTooth → single tab; needsRange-only → disabled. */
export function allowsSingleToothTab(
  capabilities: TreatAreaCapabilities,
): boolean {
  if (!canSelectTeeth(capabilities)) return false;
  return capabilities.needsTooth;
}

/** needsTooth and needsRange both enable the range tab. */
export function allowsRangeToothTab(
  capabilities: TreatAreaCapabilities,
): boolean {
  if (!canSelectTeeth(capabilities)) return false;
  return capabilities.needsTooth || capabilities.needsRange;
}

/** needsTooth multiplies fee/duration by tooth count (single or range tab). */
export function shouldMultiplyFeeAndDurationByToothCount(
  capabilities: TreatAreaCapabilities,
): boolean {
  return capabilities.needsTooth;
}

/**
 * Quad/Sextant CDT codes multiply fee/duration by the number of selected
 * quadrants or sextants (same idea as tooth-count scaling).
 * Arch and tooth-surface selections are not multiplied this way.
 */
export function shouldMultiplyFeeAndDurationByAreaCount(
  capabilities: TreatAreaCapabilities,
): boolean {
  if (
    capabilities.treatAreas.includes(ProcedureCodeTreatArea.Quad) ||
    capabilities.treatAreas.includes(ProcedureCodeTreatArea.Sextant)
  ) {
    return true;
  }

  // Non-tooth surface codes (needsSurface, no tooth/range) that expose Q/S tabs.
  if (
    capabilities.needsSurface &&
    !capabilities.needsTooth &&
    !capabilities.needsRange
  ) {
    const hasQuad = capabilities.allowedSurfaceTabs.includes("quad");
    const hasSextant = capabilities.allowedSurfaceTabs.includes("sextant");
    if (hasQuad || hasSextant) {
      return true;
    }
  }

  const hasQuad = capabilities.allowedSurfaceTabs.includes("quad");
  const hasSextant = capabilities.allowedSurfaceTabs.includes("sextant");
  const hasSurface = capabilities.allowedSurfaceTabs.includes("surface");
  // Legacy "all tabs" must not auto-multiply unless areas are already selected
  // (handled at runtime in getFeeMultiplier).
  return (hasQuad || hasSextant) && !hasSurface;
}

/**
 * needsRange alone → at least 2 teeth to be valid (1 tooth allowed as draft, flagged in UI).
 * needsTooth (with or without range) → at least 1.
 */
export function getMinTeethRequired(
  capabilities: TreatAreaCapabilities,
): number {
  if (!canSelectTeeth(capabilities)) return 0;
  if (capabilities.needsRange && !capabilities.needsTooth) return 2;
  return 1;
}

export function hasValidToothCount(
  teeth: string[],
  capabilities: TreatAreaCapabilities,
): boolean {
  if (!canSelectTeeth(capabilities)) return true;
  const count = new Set(teeth.filter(Boolean)).size;
  const min = getMinTeethRequired(capabilities);
  const max = getMaxTeethAllowed(capabilities);
  return count >= min && count <= max;
}
